import Head from 'next/head'
import ogManifest from '@lib/og-manifest.json'

export const SITE_URL = 'https://monofor.com'
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/ogImage.jpg`

// Per-page OG cards generated by `npm run generate-og`. Keyed by canonical
// path; /tr pages fall back to the EN page's card unless they have their own.
const OG_MANIFEST: Record<string, string> = ogManifest

function autoOgImage(path: string): string | undefined {
  return (
    OG_MANIFEST[path] ?? OG_MANIFEST[path.replace(/^\/tr(?=\/|$)/, '') || '/']
  )
}

// Section roots that exist as real pages, used to build BreadcrumbList
// with a linkable middle crumb. Sections without an index page fall back
// to a two-item breadcrumb (Home > page).
const BREADCRUMB_SECTIONS: Record<string, { name: string; path: string }> = {
  sign: { name: 'Monosign', path: '/sign' },
  pam: { name: 'Monopam', path: '/pam' },
  sync: { name: 'Monosync', path: '/sync' },
  learn: { name: 'Learn', path: '/learn' },
  blog: { name: 'Blog', path: '/blog' },
  glossary: { name: 'Glossary', path: '/glossary' },
  compare: { name: 'Compare', path: '/compare' },
  integrations: { name: 'Integrations', path: '/integrations' },
}

function breadcrumbJsonLd(path: string, title: string) {
  // Locale prefix is not a breadcrumb level.
  const segments = path
    .split('/')
    .filter(Boolean)
    .filter((seg, i) => !(i === 0 && seg === 'tr'))
  if (segments.length === 0) return null

  // Strip "| Monofor" / dash-separated "Monofor" style suffixes for the crumb label.
  const pageName = title.split(/ [|\u2014\u2013] /)[0].trim()

  const items: { name: string; item?: string }[] = [
    { name: 'Home', item: SITE_URL },
  ]
  const section = BREADCRUMB_SECTIONS[segments[0]]
  if (section && segments.length > 1) {
    items.push({ name: section.name, item: `${SITE_URL}${section.path}` })
  }
  items.push({ name: pageName })

  if (items.length < 2) return null
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((entry, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: entry.name,
      ...(entry.item ? { item: entry.item } : {}),
    })),
  }
}

interface SeoProps {
  title: string
  description: string
  /** Route path starting with '/', e.g. '/sign' or '/'. */
  path: string
  /** Absolute URL or site-relative path; falls back to the default OG image. */
  ogImage?: string
  ogType?: 'website' | 'article'
  noindex?: boolean
}

export default function Seo({
  title,
  description,
  path,
  ogImage,
  ogType = 'website',
  noindex = false,
}: SeoProps) {
  const url = `${SITE_URL}${path}`
  const resolvedOg = ogImage ?? autoOgImage(path)
  const image = resolvedOg
    ? resolvedOg.startsWith('http')
      ? resolvedOg
      : `${SITE_URL}${resolvedOg}`
    : DEFAULT_OG_IMAGE
  const breadcrumb = noindex ? null : breadcrumbJsonLd(path, title)

  return (
    <Head>
      {breadcrumb && (
        <script
          key="jsonld-breadcrumb"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              ...breadcrumb,
            }),
          }}
        />
      )}
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <link rel="canonical" href={url} />
      )}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Monofor" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@monoforINC" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Head>
  )
}
